<template>
  <div id="QrOrdersOilMakerByLabelMakerChart"></div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

export default {
  name: "QrOrdersOilMakerByLabelMakerChart",
  props: {
    chartData: { type: Array, required: true },
  },
  data() {
    return {
      chart: null,
      noDataLabel: null,
      colors: ["#ACF9FF", "#F2809F"],
      innerLabel: null,
    };
  },
  watch: {
    chartData: {
      handler(newData) {
        this.updateChart(newData);
      },
      immediate: true,
    },
  },
  mounted() {
    this.createChart();
    if (!this.chartData.length) {
      this.chart.data = [{ label_maker: "Unknown", value: 0, color: this.colors[4] }];
      this.chart.legend.data = this.chart.data;
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    createChart() {
      this.chart = am4core.create("QrOrdersOilMakerByLabelMakerChart", am4charts.PieChart);
      this.chart.innerRadius = am4core.percent(96);
      // this.chart.width = am4core.percent(75);
      // this.chart.height = am4core.percent(75);
      this.chart.maxWidth = 300; // or a fixed pixel value like 300
      this.chart.maxHeight = 300; // or a fixed pixel value like 300

      let pieSeries = this.chart.series.push(new am4charts.PieSeries());
      pieSeries.dataFields.value = "value";
      pieSeries.dataFields.category = "label_maker";
      pieSeries.slices.template.propertyFields.fill = "color";
      // pieSeries.slices.template.cornerRadius = 10;
      // pieSeries.slices.template.innerCornerRadius = 10;
      // pieSeries.slices.template.strokeWidth = 3;
      // pieSeries.slices.template.stroke = am4core.color("#fff");

      pieSeries.labels.template.disabled = true;
      pieSeries.ticks.template.disabled = true;

      pieSeries.slices.template.tooltipText =
        "{category}: {value} ({value.percent.formatNumber('#.0')}%)";
      pieSeries.tooltip.getFillFromObject = false;
      pieSeries.tooltip.background.fill = am4core.color("#fff");
      pieSeries.tooltip.label.fill = am4core.color("#000");
      pieSeries.tooltip.background.strokeWidth = 1;
      pieSeries.tooltip.background.stroke = am4core.color("#ccc");
      pieSeries.tooltip.background.pointerLength = 0;

      // Disable hover and click effects
      let hoverState = pieSeries.slices.template.states.getKey("hover");
      hoverState.properties.scale = 1;
      hoverState.properties.shiftRadius = 0;

      pieSeries.slices.template.clickable = false;
      pieSeries.slices.template.hoverable = false;

      this.chart.legend = new am4charts.Legend();
      this.chart.legend.position = "bottom";
      // this.chart.legend.valueLabels.template.text = "{value.percent.formatNumber('#.0')}%";
      this.chart.legend.valueLabels.template.disabled = true;
      this.chart.legend.dataFields.name = "label_maker";
      this.chart.legend.maxWidth = this.chart.pixelWidth;

      this.chart.legend.itemContainers.template.width = am4core.percent(25);
      this.chart.legend.contentAlign = "center";
      this.chart.legend.useDefaultMarker = true;
      this.chart.legend.labels.template.maxWidth = 150;
      this.chart.legend.itemContainers.template.marginBottom = 5;

      this.innerLabel = this.chart.seriesContainer.createChild(am4core.Label);
      this.innerLabel.horizontalCenter = "middle";
      this.innerLabel.verticalCenter = "middle";
      this.innerLabel.fontSize = 20;
      this.innerLabel.textAlign = "middle";
      this.innerLabel.wrap = true;
      this.innerLabel.maxWidth = 200;

      this.createNoDataLabel();
      this.updateChart(this.chartData);
    },
    createNoDataLabel() {
      if (!this.chart) return;

      this.noDataLabel = this.chart.createChild(am4core.Label);
      this.noDataLabel.align = "center";
      this.noDataLabel.valign = "middle";
      // this.noDataLabel.paddingBottom = 30;
      this.noDataLabel.verticalCenter = "middle";
      this.noDataLabel.horizontalCenter = "middle";
      this.noDataLabel.role = "noDataLabel";
      this.noDataLabel.x = am4core.percent(50);
      this.noDataLabel.y = am4core.percent(50);
      this.noDataLabel.isMeasured = false;
      this.noDataLabel.html = `
        <div class="chart-no-data">
          <span>0</span>
          <span>No Data</span> 
        </div>
        `;
      this.noDataLabel.hide();
    },
    toggleNoDataLabel(data) {
      const hasData = data && data.some((item) => item.value > 0);

      if (!hasData) {
        this.noDataLabel.show();
        this.chart.seriesContainer.hide();
        this.chart.data = [];
        this.chart.legend.hide();
      } else {
        this.noDataLabel.hide();
        this.chart.seriesContainer.show();
        this.chart.legend.show();
      }
    },
    updateChart(data = this.chartData) {
      if (!this.chart) return;

      this.toggleNoDataLabel(data);

      let mappedData = data.map((item, index) => ({
        label_maker: item.label,
        value: item.value,
        color: this.colors[index % this.colors.length],
      }));

      this.chart.data = mappedData;
      this.chart.legend.data = mappedData.filter((item) => item.value > 0);

      let total = mappedData.reduce((sum, item) => sum + item.value, 0);
      let highestCategory = mappedData[0].label_maker || "";
      let highestValue = mappedData[0].value || 0;

      mappedData.forEach((item) => {
        if (item.value > highestValue) {
          highestCategory = item.label_maker;
          highestValue = item.value;
        }
      });

      this.innerLabel.text = `[bold]${highestCategory}[/]\n${((highestValue / total) * 100).toFixed(
        2
      )}%`;
    },
  },
};
</script>

<style>
#QrOrdersOilMakerByLabelMakerChart {
  width: 100%;
  height: 320px;
  transform: translate3d(0, 0, 0);
}

#QrOrdersOilMakerByLabelMakerChart g[aria-label="Chart"] + g g[aria-labelledby] {
  opacity: 0;
}

#QrOrdersOilMakerByLabelMakerChart .chart-no-data {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  border: 16px solid #d8d8d8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 24px;
  font-weight: 600;
}

#QrOrdersOilMakerByLabelMakerChart .chart-no-data span:first-child {
  font-size: 48px;
  font-weight: 700;
}
</style>
